import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Icon, PillTab, DateTimePicker, Counter, GroupedInputBox, LabeledField, SEO } from '../../../components/common';
import TransferLanding from '../../../components/service/TransferLanding';
import { useSearchFlow, SearchFlowModals } from '../../../components/service/SearchFlow';
import SearchButton from '../../../components/service/SearchButton';
import AddressSearch from '../../../components/service/AddressSearch';
import HospitalSelector from '../../../components/service/HospitalSelector';
import { useFieldGuide } from '../../../hooks/useFieldGuide';
import useHolidayWarning from '../../../hooks/useHolidayWarning';
import HolidayWarningModal from '../../../components/service/HolidayWarningModal';
import { pricingService } from '../../../api/services';
import { loadRecentSearch, saveRecentSearch } from '../../../hooks/useRecentSearch';
import { usePendingSearch } from '../../../hooks/usePendingSearch';
import { getDefaultBookingDateTime } from '../../../utils/dateUtils';

const FIELDS = [0, 1, 2, 3];

export default function TransferHospital() {
  const { t } = useTranslation();
  const directionTabs = [
    { id: 'to-hospital', label: t('form.to_hospital') },
    { id: 'from-hospital', label: t('form.from_hospital') },
    { id: 'round-trip', label: t('form.round_trip'), icon: 'arrow-round' },
  ];
  const flow = useSearchFlow('hospital');
  const { isHighlighted, onFocusField, wrapChange, resetGuide } = useFieldGuide(FIELDS);
  const recent = loadRecentSearch('hospital', 1);
  const [direction, setDirection] = useState('to-hospital');
  const [departure, setDeparture] = useState('');
  const [departureName, setDepartureName] = useState('');
  const [departureNameI18n, setDepartureNameI18n] = useState(''); // #216: 표시 전용 현지화 명칭 (mobile/desktop 공유)
  const [hospital, setHospital] = useState('');
  const [hospitalName, setHospitalName] = useState('');
  // BugHerd #98: HospitalSelector 가 region(서울/경기 등)을 함께 제공하므로 가격 검색 시
  // destination_region 으로 사용. 미저장 시 BE 매칭 실패로 기본 fallback 가격이 반환됨.
  const [hospitalRegion, setHospitalRegion] = useState('');
  const [date, setDate] = useState(() => getDefaultBookingDateTime());
  const [people, setPeople] = useState(1);
  const [waitText, setWaitText] = useState('');

  const { holidayInfo } = useHolidayWarning(date);
  const [holidayModal, setHolidayModal] = useState(null);
  const pendingSearchRef = useRef(null);

  // Restore pending search data after login redirect
  usePendingSearch('hospital', useCallback((data) => {
    if (data.departure) setDeparture(data.departure);
    if (data.destination) setHospitalName(data.destination);
    if (data.venue) setHospital(data.venue);
    if (data.dateTime) setDate(data.dateTime);
    if (data.people) setPeople(data.people);
    if (data.direction) setDirection(data.direction);
  }, []));

  useEffect(() => {
    // Row 45: usageTimeBadge는 BE에서 한글로 내려오므로 다국어 i18n 키를 직접 사용
    setWaitText(t('form.base_time_2h'));
  }, [t]);

  return (
    <>
    <SEO serviceType="Medical Transport" renderH1 />
    <TransferLanding
      heroImg="hospital"

    >
      {waitText && <Badge variant="time">{waitText}</Badge>}

      {/* Direction Tabs */}
      <PillTab
        tabs={directionTabs}
        activeTab={direction}
        onTabChange={(val) => { setDirection(val); resetGuide(); }}
      />

      {/* Location Fields */}
      <GroupedInputBox className="lg:hidden">
        {direction === 'from-hospital' ? (
          <>
            <HospitalSelector value={hospital} onChange={(val, region, label) => { setHospital(val); setHospitalRegion(region || ''); if (label) setHospitalName(label); }} placeholder={t('form.select_hospital')} variant="borderless" />
            <AddressSearch value={departure} displayName={departureNameI18n || departureName} onChange={(address, name, nameI18n) => { setDeparture(address); setDepartureName(name || ''); setDepartureNameI18n(nameI18n || name || ''); }} placeholder={t('form.select_destination')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul_only') }} />
          </>
        ) : (
          <>
            <AddressSearch value={departure} displayName={departureNameI18n || departureName} onChange={(address, name, nameI18n) => { setDeparture(address); setDepartureName(name || ''); setDepartureNameI18n(nameI18n || name || ''); }} placeholder={t('form.select_departure')} variant="borderless" restriction={{ message: t('form.region_restriction_seoul_only') }} />
            <HospitalSelector value={hospital} onChange={(val, region, label) => { setHospital(val); setHospitalRegion(region || ''); if (label) setHospitalName(label); }} placeholder={t('form.select_hospital')} variant="borderless" />
          </>
        )}
      </GroupedInputBox>
      <div className="hidden lg:flex lg:items-start gap-2.5">
        {direction === 'from-hospital' ? (
          <>
            <LabeledField label={t('form.hospital_location')} highlighted={isHighlighted(0)} onFocusCapture={() => onFocusField(0)} hint={t('form.region_restriction_seoul_bundang')}>
              <HospitalSelector value={hospital} onChange={(val, region, label) => { wrapChange(0, setHospital)(val); setHospitalRegion(region || ''); if (label) setHospitalName(label); }} placeholder={t('form.select_hospital')} />
            </LabeledField>
            <div className="flex items-center justify-center w-6 h-12 mt-6">
              <Icon name="arrow-oneway" size={24} className="text-mono-300" />
            </div>
            <LabeledField label={t('form.destination')} highlighted={isHighlighted(1)} onFocusCapture={() => onFocusField(1)} hint={t('form.region_restriction_seoul_only')}>
              <AddressSearch
                value={departure}
                displayName={departureNameI18n || departureName}
                onChange={(address, name, nameI18n) => { wrapChange(1, setDeparture)(address); setDepartureName(name || ''); setDepartureNameI18n(nameI18n || name || ''); }}
                placeholder={t('form.select_destination')}
                restriction={{ message: t('form.region_restriction_seoul_only') }}
              />
            </LabeledField>
          </>
        ) : (
          <>
            <LabeledField label={t('form.departure')} highlighted={isHighlighted(0)} onFocusCapture={() => onFocusField(0)} hint={t('form.region_restriction_seoul_only')}>
              <AddressSearch
                value={departure}
                displayName={departureNameI18n || departureName}
                onChange={(address, name, nameI18n) => { wrapChange(0, setDeparture)(address); setDepartureName(name || ''); setDepartureNameI18n(nameI18n || name || ''); }}
                placeholder={t('form.select_departure')}
                restriction={{ message: t('form.region_restriction_seoul_only') }}
              />
            </LabeledField>
            <div className="flex items-center justify-center w-6 h-12 mt-6">
              <Icon name={direction === 'round-trip' ? 'arrow-round' : 'arrow-oneway'} size={24} className="text-mono-300" />
            </div>
            <LabeledField label={t('form.hospital_location')} highlighted={isHighlighted(1)} onFocusCapture={() => onFocusField(1)} hint={t('form.region_restriction_seoul_bundang')}>
              <HospitalSelector value={hospital} onChange={(val, region, label) => { wrapChange(1, setHospital)(val); setHospitalRegion(region || ''); if (label) setHospitalName(label); }} placeholder={t('form.select_hospital')} />
            </LabeledField>
          </>
        )}
      </div>

      {/* Row: 차량 이용 일시 + 이용 인원 */}
      <div className="flex flex-col lg:flex-row lg:items-start gap-4 lg:gap-11">
        <LabeledField label={t('form.vehicle_use_datetime')} highlighted={isHighlighted(2)} onFocusCapture={() => onFocusField(2)}>
          <DateTimePicker value={date} onChange={wrapChange(2, setDate)} maxMonths={2} blockHolidays />
        </LabeledField>
        <LabeledField label={t('form.use_passengers')} highlighted={isHighlighted(3)} onFocusCapture={() => onFocusField(3)}>
          <Counter value={people} onChange={wrapChange(3, setPeople)} />
        </LabeledField>
      </div>

      <SearchButton onClick={() => {
        if (!departure) return alert(t('form.alert_input_departure'));
        if (!hospital) return alert(t('form.alert_select_hospital'));
        if (!date) return alert(t('form.alert_select_datetime'));
        // BugHerd #87 회귀 fix: blocked + surcharge 두 케이스 모두 모달 노출.
        const proceed = () => {
          saveRecentSearch('hospital', { departure, dateTime: date, people, direction, hospitalId: hospital, hospitalName });
          flow.startSearch(date, { direction }, {
            departure, departureName,
            destination: hospitalName, destinationName: hospitalName,
            venue: hospital, venueName: hospitalName,
            // BugHerd #98: BE 가격 매칭용 region (서울/경기 등). HospitalRegion.name 과 일치.
            hospitalRegion,
            direction, people,
          });
        };
        if (holidayInfo?.blocked) { setHolidayModal(holidayInfo); return; }
        if (holidayInfo?.amount > 0) { pendingSearchRef.current = proceed; setHolidayModal(holidayInfo); return; }
        proceed();
      }} />
    </TransferLanding>
    <SearchFlowModals flow={flow} dateTime={date} />
    {holidayModal && <HolidayWarningModal
      holiday={holidayModal}
      onClose={() => setHolidayModal(null)}
      onConfirm={() => { const fn = pendingSearchRef.current; pendingSearchRef.current = null; fn?.(); }}
    />}
    </>
  );
}
